import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  BaseSelect,
  cn,
  Loader,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sib/ui';

export interface SelectOption {
  label: string | ReactNode;
  Icon?: ReactNode;
  triggerLabel?: string;
  value: string | number | null;
  disabled?: boolean;
  hidden?: boolean;
}

export type SelectProps = ComponentProps<typeof BaseSelect> & {
  options: SelectOption[];
  placeholder?: string;
  selectTriggerProps?: ComponentProps<typeof SelectTrigger>;
  selectValueProps?: ComponentProps<typeof SelectValue>;
  selectContentProps?: ComponentProps<typeof SelectContent>;
  selectItemProps?: Omit<ComponentProps<typeof SelectItem>, 'value'>;
  loading?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  onItemSelect?: (value: string, index: number) => void;
};

const Select = forwardRef<HTMLSpanElement, SelectProps>(
  (
    {
      options,
      selectContentProps,
      selectItemProps,
      selectTriggerProps,
      selectValueProps,
      placeholder,
      loading,
      clearable,
      onClear,
      onItemSelect,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(props.open);

    const selectableOptions = useMemo(
      () => options.filter(option => option.value !== null),
      [options]
    );

    const selectedValue = useMemo(() => {
      const selected = selectableOptions.find(
        ({ value }) => value === props.value
      );

      if (!selected) return null;

      const value = selected?.triggerLabel || selected?.label || null;

      return (
        <span className="inline-flex items-center gap-2">
          {selected?.Icon}
          {value}
        </span>
      );
    }, [selectableOptions, props.value]);

    const handleItemPointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.open === undefined) setOpen(false);

        if (onClear) return onClear();

        props.onValueChange?.('');
      },
      [onClear, props]
    );

    return (
      <BaseSelect
        {...props}
        value={props.value == null ? '' : String(props.value)}
        onValueChange={val => {
          if (!val) return;

          props.onValueChange?.(val);
        }}
        {...(props.open === undefined && {
          open,
          onOpenChange: value => {
            setOpen(value);
            props.onOpenChange?.(value);
          },
        })}
      >
        <SelectTrigger
          {...selectTriggerProps}
          className={cn(
            selectTriggerProps?.className,
            '[&>span]:w-[calc(100%-16px)] [&>span]:text-left'
          )}
          disabled={props.disabled || loading}
          {...(loading && {
            icon: <Loader />,
          })}
        >
          <SelectValue
            ref={ref}
            placeholder={placeholder}
            {...selectValueProps}
          >
            {selectedValue}
            {selectTriggerProps?.children}
          </SelectValue>
        </SelectTrigger>
        {selectableOptions.length > 0 && (props?.open || open) && (
          <SelectContent {...selectContentProps}>
            {selectableOptions.map(
              ({ value: rawValue, label, disabled, hidden, Icon }, index) => {
                const isSelected = rawValue === props.value;
                const value = String(rawValue);

                return (
                  <SelectItem
                    {...selectItemProps}
                    value={value}
                    key={`select-item-${index}-${value}`}
                    className={cn(
                      disabled && 'text-muted-foreground pointer-events-none',
                      hidden && 'hidden',
                      selectItemProps?.className
                    )}
                    {...(clearable &&
                      isSelected && {
                        onPointerDown: handleItemPointerDown,
                      })}
                    {...(onItemSelect && {
                      onPointerDown: (e: React.PointerEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onItemSelect(value, index);
                      },
                    })}
                  >
                    <div className="flex flex-row items-center gap-2">
                      {Icon}
                      {label}
                    </div>
                  </SelectItem>
                );
              }
            )}
            {selectContentProps?.children}
          </SelectContent>
        )}
      </BaseSelect>
    );
  }
);

export { Select };
